import { Box, useColorScheme } from "@mui/joy";
import { useParams } from "react-router-dom";
import { useProject } from "../../../../hooks/query";

/**
 * A Google Maps iframe rendered as a heavily faded, blurred background hero
 * behind the SummaryCard. Adapts to light/dark mode automatically.
 */
export function MapHero() {
    const { id } = useParams<{ id: string }>();
    const { useGetProjectById } = useProject();
    const { data: details } = useGetProjectById(id!);
    const { mode } = useColorScheme();

    const isDark = mode === "dark";

    const address = details
        ? `${details.CustomerStreet}, ${details.CustomerCity}, ${details.CustomerZipCode}`
        : "Colorado";

    const encodedAddress = encodeURIComponent(address);
    const mapUrl = `https://maps.google.com/maps?q=${encodedAddress}&t=&z=14&ie=UTF8&iwloc=&output=embed`;

    // Light: subtle blur + slight desaturation
    // Dark:  invert + hue-rotate flips the map to dark tones
    const mapFilter = isDark
        ? "blur(1px) invert(1) hue-rotate(180deg) grayscale(0.3) brightness(0.8)"
        : "blur(1px) grayscale(0.7) brightness(1.05)";

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: { xs: 120, md: 140 },
                zIndex: 1,
                overflow: "hidden",
                pointerEvents: "none",
            }}
        >
            {/* Map iframe — filters adapt to color scheme */}
            <Box
                component="iframe"
                src={mapUrl}
                title="Project Location"
                sx={{
                    position: "absolute",
                    top: -15,
                    left: -15,
                    right: -15,
                    bottom: -15,
                    width: "calc(100% + 30px)",
                    height: "calc(100% + 30px)",
                    border: "none",
                    filter: mapFilter,
                    opacity: isDark ? 0.3 : 0.25,
                    pointerEvents: "none",
                }}
            />

            {/* Gradient fade — bottom edge blends into page background */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: (theme) =>
                        `linear-gradient(180deg, 
                            transparent 0%, 
                            ${theme.vars.palette.background.surface} 80%)`,
                }}
            />
        </Box>
    );
}
